export enum DryFormulationUnitOfMeasure {
  GRAM = 'g',
  KILOGRAM = 'kg',
  OUNCE = 'oz',
  POUND = 'lb',
  TON = 'ton',
}

export enum LiquidFormulationUnitOfMeasure {
  CENTILITRE = 'cl',
  DECILITRE = 'dl',
  FLUID_OUNCE = 'fl oz',
  GALLON = 'gal',
  KILOLITRE = 'kl',
  LITRE = 'l',
  MILLILITRE = 'ml',
  PINT = 'pt',
  QUART = 'qt',
  TOTE = 'tote',
}

export enum SeedApplicationUnitOfMeasure {
  MSDS = 'msds',
  KSDS = 'ksds',
  LBS = 'lbs',
}

export enum SeedProductUnitOfMeasure {
  POUND = 'lb',
  BAG = 'bag',
  BUSHEL = 'bu',
}

export enum ServiceUnitOfMeasure {
  ACRE = 'ac',
}

export const ProductUomEnums = [
  DryFormulationUnitOfMeasure,
  LiquidFormulationUnitOfMeasure,
  SeedProductUnitOfMeasure,
  ServiceUnitOfMeasure,
];

export type ProductUom =
  SeedProductUnitOfMeasure
  | LiquidFormulationUnitOfMeasure
  | DryFormulationUnitOfMeasure
  | ServiceUnitOfMeasure;

export const DryFormulationSpellings: Record<DryFormulationUnitOfMeasure, string[]> = {
  [DryFormulationUnitOfMeasure.GRAM]: [
    DryFormulationUnitOfMeasure.GRAM,
    'g',
    'gr',
    'gm',
    'grm',
    'gram',
    'grams',
  ],
  [DryFormulationUnitOfMeasure.KILOGRAM]: [
    DryFormulationUnitOfMeasure.KILOGRAM,
    'kg',
    'kgs',
    'kilo',
    'kilos',
    'kilogram',
    'kilograms',
    'kilogramme',
    'kilogrammes',
  ],
  [DryFormulationUnitOfMeasure.OUNCE]: [
    DryFormulationUnitOfMeasure.OUNCE,
    'oz',
    'ounce',
    'ounces',
  ],
  [DryFormulationUnitOfMeasure.POUND]: [
    DryFormulationUnitOfMeasure.POUND,
    'lb',
    'lbs',
    'pound',
    'pounds',
  ],
  [DryFormulationUnitOfMeasure.TON]: [
    DryFormulationUnitOfMeasure.TON,
    'tn',
    'ton',
    'tons',
  ],
};

export const LiquidFormulationSpellings: Record<LiquidFormulationUnitOfMeasure, string[]> = {
  [LiquidFormulationUnitOfMeasure.CENTILITRE]: [
    LiquidFormulationUnitOfMeasure.CENTILITRE,
    'cl',
    'cen',
    'centilitre',
    'centilitres',
    'centiliter',
    'centiliters',
  ],
  [LiquidFormulationUnitOfMeasure.DECILITRE]: [
    LiquidFormulationUnitOfMeasure.DECILITRE,
    'dl',
    'dlt',
    'decilitre',
    'decilitres',
    'deciliter',
    'deciliters',
  ],
  [LiquidFormulationUnitOfMeasure.FLUID_OUNCE]: [
    LiquidFormulationUnitOfMeasure.FLUID_OUNCE,
    'fl oz',
    'oz fl',
    'fluid ounce',
    'fluid ounces',
  ],
  [LiquidFormulationUnitOfMeasure.GALLON]: [
    LiquidFormulationUnitOfMeasure.GALLON,
    'ga',
    'gl',
    'gal',
    'gals',
    'gallon',
    'gallons',
  ],
  [LiquidFormulationUnitOfMeasure.KILOLITRE]: [
    LiquidFormulationUnitOfMeasure.KILOLITRE,
    'kl',
    '㎘',
    'kilolitre',
    'kilolitres',
    'kiloliter',
    'kiloliters',
  ],
  [LiquidFormulationUnitOfMeasure.LITRE]: [
    LiquidFormulationUnitOfMeasure.LITRE,
    'l',
    'lt',
    'ltr',
    'ltrs',
    'litre',
    'litres',
    'liter',
    'liters',
    'ℓ',
    'dm³',
    'dm^3',
    '1000 cm³',
    '1000 cm^3',
    '1000 cc',
  ],
  [LiquidFormulationUnitOfMeasure.MILLILITRE]: [
    LiquidFormulationUnitOfMeasure.MILLILITRE,
    'ml',
    'mlt',
    'cm³',
    'cm^3',
    'cc',
    '0.001 L',
    'millilitre',
    'millilitres',
    'milliliter',
    'milliliters',
  ],
  [LiquidFormulationUnitOfMeasure.PINT]: [
    LiquidFormulationUnitOfMeasure.PINT,
    'p',
    'pt',
    'pts',
    'pint',
    'pints',
  ],
  [LiquidFormulationUnitOfMeasure.QUART]: [
    LiquidFormulationUnitOfMeasure.QUART,
    'qt',
    'qts',
    'quart',
    'quarts',
  ],
  [LiquidFormulationUnitOfMeasure.TOTE]: [
    LiquidFormulationUnitOfMeasure.TOTE,
    'tote',
    'totes',
  ],
};

export const parseDryFormulationUom = (uom: string): DryFormulationUnitOfMeasure | null => {
  const dryValues: DryFormulationUnitOfMeasure[] = Object.values(DryFormulationUnitOfMeasure);
  for (const dryValue of dryValues) {
    const mappedUomValues = DryFormulationSpellings[dryValue];
    const normalizedSpellings = mappedUomValues.map((spelling) =>
      spelling.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );
    const mappingRegex = new RegExp(`^(${normalizedSpellings.join('|')})$`, 'i');
    if (mappingRegex.test(uom.replace(/\.$/, ""))) {
      return dryValue as DryFormulationUnitOfMeasure;
    }
  }
  return null;
};

export const parseLiquidFormulationUom = (uom: string): LiquidFormulationUnitOfMeasure | null => {
  const liquidValues: LiquidFormulationUnitOfMeasure[] =
    Object.values(LiquidFormulationUnitOfMeasure);
  for (const liquidValue of liquidValues) {
    const mappedUomValues = LiquidFormulationSpellings[liquidValue];
    const normalizedSpellings = mappedUomValues.map((spelling) =>
      spelling.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );
    const mappingRegex = new RegExp(`^(${normalizedSpellings.join('|')})$`, 'i');
    if (mappingRegex.test(uom.replace(/\.$/, ""))) {
      return liquidValue as LiquidFormulationUnitOfMeasure;
    }
  }
  return null;
};
